const ORBE_MODAL_SELECTOR = '#md-app-embed__modal'
const ORBE_FORM_SELECTOR = 'form[action="/localization"]'
const ORBE_SUBMIT_SELECTOR = '#md-btn__form__onSubmit'
const SYNC_KEY = 'WEGLOT_ORBE_SYNC'
const WEGLOT_POLL_MS = 100
const WEGLOT_POLL_MAX = 50

let weglotListenersAttached = false
let weglotInitHandlerRegistered = false

function normalizeLangCode(langCode) {
  if (langCode == null || langCode === '') { return null }

  return `${langCode}`.toLowerCase().split('-')[0]
}

function isWeglotReady() {
  return typeof Weglot !== 'undefined' && Weglot.initialized === true
}

function getShopifyLanguageCode() {
  const el = document.querySelector('[data-shopify-language-code]')
  return normalizeLangCode(el?.dataset.shopifyLanguageCode)
}

function getOriginalLanguageCode() {
  if (typeof Weglot !== 'undefined' && Weglot.options?.language_from) {
    return normalizeLangCode(Weglot.options.language_from)
  }

  return getShopifyLanguageCode() || 'en'
}

function getWeglotLanguageCode() {
  if (!isWeglotReady()) { return null }

  return normalizeLangCode(Weglot.getCurrentLang())
}

function getPathPrefix(pathname = window.location.pathname) {
  const match = pathname.match(/^\/([a-z]{2})(?=\/|$)/)
  return match ? normalizeLangCode(match[1]) : null
}

function getDestinationLanguageCodes() {
  const originalLang = getOriginalLanguageCode()

  if (isWeglotReady()) {
    const languages = Weglot.getAvailableLanguages().map(normalizeLangCode).filter(Boolean)
    if (!languages.includes(originalLang)) {
      languages.push(originalLang)
    }

    return languages.filter((lang) => lang !== originalLang)
  }

  const codes = new Set()
  ;[
    getPathPrefix(),
    getShopifyLanguageCode(),
    normalizeLangCode(sessionStorage.getItem(SYNC_KEY)),
  ].forEach((lang) => {
    if (lang && lang !== originalLang) {
      codes.add(lang)
    }
  })

  return [...codes]
}

function getUrlLanguageCode() {
  const pathLang = getPathPrefix()
  if (pathLang && getDestinationLanguageCodes().includes(pathLang)) {
    return pathLang
  }

  return getOriginalLanguageCode()
}

function getPathDisplayLanguageCode() {
  const pathLang = getPathPrefix()
  if (pathLang && getDestinationLanguageCodes().includes(pathLang)) {
    return pathLang
  }

  return null
}

function getDisplayLanguageCode() {
  return getPathDisplayLanguageCode()
    || getWeglotLanguageCode()
    || getShopifyLanguageCode()
}

function resolveLanguage(rawValue) {
  const normalized = normalizeLangCode(rawValue)
  if (normalized) { return normalized }

  if (rawValue !== '') { return null }

  if (typeof Weglot !== 'undefined' && Weglot.options?.language_from) {
    return normalizeLangCode(Weglot.options.language_from)
  }

  return isWeglotReady() ? getOriginalLanguageCode() : null
}

function isLanguageOutOfSync(targetLang) {
  const target = normalizeLangCode(targetLang)
  if (!target) { return false }

  const weglotLang = getWeglotLanguageCode()
  const urlLang = getUrlLanguageCode()

  if (weglotLang === target && urlLang === target) { return false }
  if (weglotLang && weglotLang !== target) { return true }
  if (urlLang !== target) { return true }

  if (!weglotLang) {
    const shopifyLang = getShopifyLanguageCode()
    if (shopifyLang && shopifyLang !== target) { return true }
  }

  return false
}

function stripLanguagePrefix(pathname) {
  const pathLang = getPathPrefix(pathname)
  if (!pathLang || !getDestinationLanguageCodes().includes(pathLang)) {
    return pathname || '/'
  }

  const stripped = pathname.replace(new RegExp(`^/${pathLang}(?=/|$)`), '') || '/'
  return stripped.startsWith('/') ? stripped : `/${stripped}`
}

function buildPathForLanguage(targetLang, pathname) {
  const target = normalizeLangCode(targetLang)
  const stripped = stripLanguagePrefix(pathname)

  if (!target || target === getOriginalLanguageCode()) {
    return stripped || '/'
  }

  if (stripped === '/') {
    return `/${target}`
  }

  return `/${target}${stripped}`
}

function redirectToLanguageUrlIfNeeded(targetLang) {
  const target = normalizeLangCode(targetLang)
  if (!target || getUrlLanguageCode() === target) { return false }

  const nextUrl = new URL(window.location.href)
  nextUrl.pathname = buildPathForLanguage(target, nextUrl.pathname)
  window.location.assign(nextUrl.toString())
  return true
}

function getLanguageNameFromCatalog(langCode, displayRoot) {
  const catalog = displayRoot?.dataset.availableLanguages
  if (!catalog) { return null }

  const normalized = normalizeLangCode(langCode)
  if (!normalized) { return null }

  const entries = catalog.split('|').map((part) => {
    const colonIndex = part.indexOf(':')
    if (colonIndex === -1) { return null }

    return {
      isoCode: part.slice(0, colonIndex),
      name: part.slice(colonIndex + 1) || null,
    }
  }).filter(Boolean)

  const exact = entries.find((entry) => entry.isoCode.toLowerCase() === `${langCode}`.toLowerCase())
  if (exact?.name) { return exact.name }

  const base = entries.find((entry) => normalizeLangCode(entry.isoCode) === normalized)
  return base?.name || null
}

function getLanguageDisplayName(langCode, displayRoot) {
  const normalized = normalizeLangCode(langCode)
  if (!normalized) { return null }

  if (isWeglotReady()) {
    return Weglot.getLanguageName(normalized)
  }

  return getLanguageNameFromCatalog(normalized, displayRoot)
}

function updateLanguageNameLabels() {
  document.querySelectorAll('[data-country-selector-display]').forEach((displayRoot) => {
    const { countryCode, currencySymbol } = displayRoot.dataset
    const languageName = getLanguageDisplayName(getDisplayLanguageCode(), displayRoot)

    if (!countryCode || !currencySymbol || !languageName) { return }

    const nextLabel = `${countryCode} ${languageName} (${currencySymbol})`
    if (displayRoot.textContent !== nextLabel) {
      displayRoot.textContent = nextLabel
    }
  })
}

function clearSync() {
  sessionStorage.removeItem(SYNC_KEY)
}

function getPendingSyncTarget() {
  if (sessionStorage.getItem(SYNC_KEY) === null) { return null }

  return resolveLanguage(sessionStorage.getItem(SYNC_KEY))
}

function completeSyncIfMatched(langCode) {
  if (sessionStorage.getItem(SYNC_KEY) === null) { return }

  const target = getPendingSyncTarget()
  if (target && normalizeLangCode(langCode) === target && !isLanguageOutOfSync(target)) {
    clearSync()
  }
}

function getOrbeLanguageFromForm(orbeForm) {
  const toggle = orbeForm.querySelector('button[name="md-form__select__language__button"]')?.dataset?.value
  if (toggle) { return normalizeLangCode(toggle) }

  const selectedLink = orbeForm.querySelector(
    '.md-form__select__language__list-link[aria-selected="true"], .md-form__select__language__list-link--selected, .md-form__select__language__list-link.active'
  )
  const selected = selectedLink?.dataset?.value ?? selectedLink?.getAttribute('data-value')
  if (selected) { return normalizeLangCode(selected) }

  const input = orbeForm.querySelector('input[name="language_code"]')
  if (input) {
    return input.value === '' ? '' : normalizeLangCode(input.value)
  }

  return null
}

function captureOrbeFormLanguage(event) {
  const orbeForm = event.target.closest?.(ORBE_FORM_SELECTOR)
  if (!orbeForm?.closest(ORBE_MODAL_SELECTOR)) { return }

  const formLang = getOrbeLanguageFromForm(orbeForm)
  if (formLang === null) { return }

  const target = resolveLanguage(formLang)
  if (!target || !isLanguageOutOfSync(target)) { return }

  sessionStorage.setItem(SYNC_KEY, formLang)
}

function attachOrbeFormListeners() {
  if (document.body.dataset.weglotOrbeSyncAttached === 'true') { return }

  document.body.dataset.weglotOrbeSyncAttached = 'true'
  document.body.addEventListener('click', (event) => {
    if (event.target.closest?.(ORBE_SUBMIT_SELECTOR)) {
      captureOrbeFormLanguage(event)
    }
  }, true)
  document.body.addEventListener('submit', captureOrbeFormLanguage, true)
}

function isSupportedWeglotLanguage(targetLang) {
  if (!isWeglotReady()) { return true }

  const originalLang = getOriginalLanguageCode()
  const languages = Weglot.getAvailableLanguages().map(normalizeLangCode).filter(Boolean)
  if (!languages.includes(originalLang)) {
    languages.push(originalLang)
  }

  return languages.includes(normalizeLangCode(targetLang))
}

function runSync() {
  updateLanguageNameLabels()

  const target = getPendingSyncTarget()
  if (!target) { return }

  if (isWeglotReady() && !isSupportedWeglotLanguage(target)) {
    clearSync()
    return
  }

  if (redirectToLanguageUrlIfNeeded(target)) { return }

  if (!isLanguageOutOfSync(target)) {
    clearSync()
    return
  }

  if (!isWeglotReady()) { return }

  Weglot.switchTo(target)
  updateLanguageNameLabels()

  if (!isLanguageOutOfSync(target)) {
    clearSync()
  }
}

function attachWeglotListeners() {
  if (weglotListenersAttached || typeof Weglot === 'undefined') { return }

  weglotListenersAttached = true
  Weglot.on('languageChanged', (lang) => {
    updateLanguageNameLabels()
    completeSyncIfMatched(lang)
  })
  Weglot.on('switchersReady', updateLanguageNameLabels)
}

function whenWeglotReady(callback) {
  if (typeof Weglot === 'undefined') { return false }

  attachWeglotListeners()

  if (Weglot.initialized) {
    callback()
  } else if (!weglotInitHandlerRegistered) {
    weglotInitHandlerRegistered = true
    Weglot.on('initialized', callback)
  }

  return true
}

function bootstrapWeglot() {
  if (whenWeglotReady(runSync)) { return }

  const weglotScript = document.querySelector('script[src*="weglot"]')
  if (!weglotScript) {
    if (sessionStorage.getItem(SYNC_KEY) !== null) { clearSync() }
    return
  }

  const start = () => {
    if (!whenWeglotReady(runSync)) {
      let attempts = 0
      const poll = setInterval(() => {
        if (whenWeglotReady(runSync)) {
          clearInterval(poll)
        } else if (++attempts >= WEGLOT_POLL_MAX) {
          clearInterval(poll)
          if (sessionStorage.getItem(SYNC_KEY) !== null) { clearSync() }
        }
      }, WEGLOT_POLL_MS)
    }
  }

  if (weglotScript.complete || weglotScript.readyState === 'complete' || weglotScript.readyState === 'loaded') {
    start()
  } else {
    weglotScript.addEventListener('load', start)
    weglotScript.addEventListener('error', () => {
      if (sessionStorage.getItem(SYNC_KEY) !== null) { clearSync() }
    })
  }
}

function initOrbeWeglotSync() {
  updateLanguageNameLabels()
  attachOrbeFormListeners()

  const target = getPendingSyncTarget()
  if (target && redirectToLanguageUrlIfNeeded(target)) { return }

  bootstrapWeglot()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOrbeWeglotSync)
} else {
  initOrbeWeglotSync()
}
